import { useEffect, useState } from "react";
import { api, fmt, formatErr, validityStatus, toneClass, openDataUrlInTab, downloadDataUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Wallet, TrendingUp, Route, PiggyBank, ArrowDownToLine, MapPin, ScrollText, Phone, MessageCircle, CalendarClock, CheckCircle2, XCircle, Clock, FileText, Download, ExternalLink, Send, Gift, Users as UsersIcon, ClipboardList, Ban } from "lucide-react";

export default function DriverPortal() {
  const [s, setS] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [open, setOpen] = useState(false);
  const [amt, setAmt] = useState(0);
  const [note, setNote] = useState("");
  const [submitOpen, setSubmitOpen] = useState(false);
  const [sub, setSub] = useState({
    customer_name: "", customer_contact: "", no_of_days: 1, no_of_passengers: 1,
    trip_offer_value: 0, pickup_location: "", drop_location: "",
    trip_date: new Date().toISOString().slice(0, 10),
  });

  async function load() {
    const [sum, a] = await Promise.all([api.get("/driver/summary"), api.get("/driver-assignments").catch(() => ({ data: [] }))]);
    setS(sum.data); setAssignments(a.data || []);
  }
  useEffect(() => { load(); }, []);

  async function submitTrip(e) {
    e.preventDefault();
    try {
      await api.post("/driver-trips", {
        ...sub,
        no_of_days: parseInt(sub.no_of_days) || 1,
        no_of_passengers: parseInt(sub.no_of_passengers) || 1,
        trip_offer_value: parseFloat(sub.trip_offer_value) || 0,
      });
      toast.success("Trip submitted — waiting for admin confirmation");
      setSubmitOpen(false);
      setSub({ customer_name: "", customer_contact: "", no_of_days: 1, no_of_passengers: 1,
        trip_offer_value: 0, pickup_location: "", drop_location: "",
        trip_date: new Date().toISOString().slice(0, 10) });
      load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Failed to submit"); }
  }

  async function withdraw(e) {
    e.preventDefault();
    if (parseFloat(amt) > (s.available_savings || 0)) return toast.error("Amount exceeds available savings");
    try { await api.post("/driver/withdraw", { amount: parseFloat(amt), note });
      toast.success("Request sent for admin approval");
      setOpen(false); setAmt(0); setNote(""); load();
    } catch (err) { toast.error(formatErr(err.response?.data?.detail) || "Failed"); }
  }

  if (!s) return <div className="text-muted-foreground">Loading…</div>;
  if (!s.driver) return <Card className="p-8 bg-[hsl(224_45%_9%)] border-border">Your driver record has not been linked yet. Contact the fleet admin.</Card>;

  const d = s.driver;
  const dlStatus = validityStatus(d.dl_validity);
  const dlExpiringSoon = (() => {
    if (!d.dl_validity) return false;
    const dt = new Date(d.dl_validity);
    const days = Math.ceil((dt - new Date()) / (86400000));
    return days > 0 && days <= 30;
  })();

  const now = new Date();
  const upcoming = (assignments || []).filter((a) => {
    if (a.status === "completed" || a.status === "cancelled") return false;
    if (!a.trip_date) return true;
    return new Date(a.trip_date) >= new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  });

  const withStatusTone = (st) => ({
    approved: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
    rejected: "bg-red-500/15 text-red-300 border-red-500/40",
    pending: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  }[st || "pending"]);
  const withStatusIcon = (st) => st === "approved" ? <CheckCircle2 className="w-3 h-3"/> : st === "rejected" ? <XCircle className="w-3 h-3"/> : <Clock className="w-3 h-3"/>;

  return (
    <div className="space-y-6">
      {/* Profile header */}
      <Card className="p-6 bg-[hsl(224_45%_9%)] border-border">
        <div className="flex flex-wrap items-center gap-6">
          {d.photo_url ? <img src={d.photo_url} alt="" className="w-24 h-24 rounded-full object-cover border-2 border-amber-500/40"/>
            : <div className="w-24 h-24 rounded-full bg-slate-700 grid place-items-center text-3xl font-bold">{d.name?.[0]}</div>}
          <div className="flex-1 min-w-[240px]">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Driver profile</div>
            <h1 className="font-display text-3xl font-bold">{d.name}</h1>
            <div className="grid md:grid-cols-3 gap-3 mt-3 text-sm">
              <div>
                <div className="text-[10px] uppercase text-muted-foreground">DL Number</div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-amber-300">{d.dl_number || "—"}</span>
                  {d.dl_verified && <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/40 text-[10px]">✓ Verified</Badge>}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-muted-foreground">DL valid till</div>
                <div className="flex items-center gap-2">
                  <span className="font-mono">{d.dl_validity || "—"}</span>
                  {d.dl_validity && <span className={`text-[10px] px-1.5 py-0.5 rounded border ${toneClass(dlStatus.tone)}`}>{dlStatus.label}</span>}
                </div>
                {dlExpiringSoon && (
                  <div className="text-[10px] text-amber-300 mt-1">⚠ DL expires within a month — renew soon</div>
                )}
              </div>
              <div>
                <div className="text-[10px] uppercase text-muted-foreground">Aadhaar</div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs">{d.aadhaar || "—"}</span>
                  {d.aadhaar_verified && <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/40 text-[10px]">✓ Verified</Badge>}
                </div>
              </div>
              <div><div className="text-[10px] uppercase text-muted-foreground">Mobile</div><div className="font-mono">{d.mobile || "—"}</div></div>
              <div><div className="text-[10px] uppercase text-muted-foreground">WhatsApp</div><div className="font-mono text-emerald-300">{d.whatsapp || "—"}</div></div>
              <div className="md:col-span-1"><div className="text-[10px] uppercase text-muted-foreground">Address</div><div className="text-sm">{d.address || "—"}</div></div>
            </div>
          </div>
        </div>
      </Card>

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {[
          { icon: PiggyBank, label: "Total saving balance", value: fmt(s.available_savings), sub: `Withdrawn ${fmt(s.withdrawn)} · Pending ${fmt(s.pending_withdraw || 0)}`, tone: "text-amber-400" },
          { icon: TrendingUp, label: "Total earnings", value: fmt(s.earnings), tone: "text-emerald-400" },
          { icon: Gift, label: "Bonus earned", value: fmt(s.bonus_total || 0), tone: "text-pink-400", sub: `${(s.submitted_trips || []).filter((x) => x.status === "confirmed").length} confirmed trips` },
          { icon: Route, label: "Total trips", value: s.trips.length, tone: "text-sky-400" },
          { icon: Wallet, label: "Payable to me", value: fmt(s.payable_to_driver), tone: "text-emerald-400", sub: "Includes bonus" },
          { icon: Wallet, label: "Receivable from me", value: fmt(s.receivable_from_driver), tone: "text-rose-400" },
        ].map((k, i) => (
          <Card key={i} className="p-5 bg-[hsl(224_45%_9%)] border-border card-hover">
            <div className={`w-10 h-10 rounded-lg bg-white/5 grid place-items-center ${k.tone}`}><k.icon className="w-5 h-5"/></div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mt-3">{k.label}</div>
            <div className="font-display text-2xl font-bold mt-1">{k.value}</div>
            {k.sub && <div className="text-[10px] text-muted-foreground mt-1">{k.sub}</div>}
          </Card>
        ))}
      </div>

      {/* My documents */}
      <Card className="p-6 bg-[hsl(224_45%_9%)] border-border" data-testid="dp-documents">
        <div className="flex items-center gap-2 mb-4 text-amber-400">
          <FileText className="w-4 h-4"/>
          <div className="font-display text-xl font-bold">My documents</div>
          <Badge variant="outline" className="ml-auto border-amber-500/30 text-amber-300 text-xs">Uploaded by admin</Badge>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            { key: "dl_image", label: "Driving Licence", meta: d.dl_number, badge: d.dl_verified },
            { key: "aadhaar_image", label: "Aadhaar card", meta: d.aadhaar, badge: d.aadhaar_verified },
            { key: "authority_letter_image", label: "Authority letter", meta: null, badge: !!d.authority_letter_image },
          ].map((doc) => {
            const v = d[doc.key];
            const isPdf = v && v.startsWith("data:application/pdf");
            return (
              <div key={doc.key} data-testid={`dp-doc-${doc.key}`}
                className="p-3 rounded-lg border border-border bg-[hsl(224_40%_11%)] flex flex-col">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">{doc.label}</div>
                  {v ? (doc.badge ? <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/40 text-[10px]">Verified</Badge>
                                   : <Badge className="bg-slate-500/15 text-slate-300 border-slate-500/40 text-[10px]">Uploaded</Badge>)
                      : <Badge className="bg-rose-500/15 text-rose-300 border-rose-500/40 text-[10px]">Missing</Badge>}
                </div>
                {v ? (
                  <>
                    {isPdf ? (
                      <button type="button" onClick={() => openDataUrlInTab(v)} className="flex-1 grid place-items-center h-32 rounded-md border border-border bg-[hsl(224_40%_10%)] hover:border-amber-500/40" data-testid={`dp-doc-preview-${doc.key}`}>
                        <FileText className="w-10 h-10 text-sky-300"/>
                      </button>
                    ) : (
                      <button type="button" onClick={() => openDataUrlInTab(v)} className="block w-full" data-testid={`dp-doc-preview-${doc.key}`}>
                        <img src={v} alt="" className="w-full h-32 object-cover rounded-md border border-border hover:border-amber-500/40 transition-colors"/>
                      </button>
                    )}
                    {doc.meta && <div className="font-mono text-xs text-amber-300 mt-2 truncate">{doc.meta}</div>}
                    <div className="mt-2 flex gap-2">
                      <button type="button" onClick={() => openDataUrlInTab(v)} data-testid={`dp-doc-view-${doc.key}`}
                        className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md border border-border hover:border-amber-500/40 text-xs">
                        <ExternalLink className="w-3 h-3"/> View
                      </button>
                      <button type="button" onClick={() => downloadDataUrl(v, `${doc.label.toLowerCase().replace(/\s/g, "-")}${isPdf ? ".pdf" : ".png"}`)}
                        data-testid={`dp-doc-download-${doc.key}`}
                        className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md border border-border hover:border-emerald-500/40 hover:text-emerald-300 text-xs">
                        <Download className="w-3 h-3"/> Download
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 text-xs text-muted-foreground py-8 text-center">Admin hasn't uploaded this yet. Please request it.</div>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      {/* Withdraw request */}
      <Card className="p-6 bg-[hsl(224_45%_9%)] border-border">
        <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
          <div>
            <div className="font-display text-xl font-bold">Withdraw savings</div>
            <div className="text-sm text-muted-foreground">Available now: <span className="text-amber-300 font-mono">{fmt(s.available_savings)}</span> · pending admin approval: <span className="font-mono">{fmt(s.pending_withdraw || 0)}</span></div>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="dp-withdraw" className="bg-amber-500 text-[hsl(224_60%_6%)] hover:bg-amber-400 font-semibold gap-2"><ArrowDownToLine className="w-4 h-4"/> Request withdrawal</Button>
            </DialogTrigger>
            <DialogContent className="bg-[hsl(224_45%_9%)] border-border">
              <DialogHeader><DialogTitle className="font-display text-2xl">Request withdrawal</DialogTitle></DialogHeader>
              <form onSubmit={withdraw} className="space-y-3">
                <div><Label className="text-[10px] uppercase text-muted-foreground">Amount ₹</Label>
                  <Input required type="number" max={s.available_savings} value={amt} onChange={(e) => setAmt(e.target.value)} data-testid="dp-amount" className="bg-[hsl(224_40%_12%)] mt-1"/></div>
                <div><Label className="text-[10px] uppercase text-muted-foreground">Note (optional)</Label>
                  <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why do you need this now?" className="bg-[hsl(224_40%_12%)] mt-1"/></div>
                <div className="text-[11px] text-amber-200/80 rounded-md border border-amber-500/30 bg-amber-500/10 p-2">
                  Your request will wait for admin approval. Once approved, the balance auto-updates.
                </div>
                <Button type="submit" data-testid="dp-confirm" className="w-full bg-amber-500 text-[hsl(224_60%_6%)] hover:bg-amber-400 font-semibold">Send request</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        {s.withdrawals.length > 0 && (
          <div className="-mx-6 md:mx-0 overflow-x-auto">
            <table className="w-full min-w-[560px] text-sm">
              <thead><tr className="text-[10px] uppercase tracking-wider text-muted-foreground bg-[hsl(224_45%_9%)]">
                <th className="text-left p-2 sticky left-0 z-10 bg-[hsl(224_45%_9%)]">Date</th><th className="text-left p-2">Amount</th><th className="text-left p-2">Note</th><th className="text-left p-2">Status</th>
              </tr></thead>
              <tbody>{s.withdrawals.map((w) => (
                <tr key={w.id} className="border-t border-border bg-[hsl(224_45%_9%)]" data-testid={`wd-${w.id}`}>
                  <td className="p-2 text-xs sticky left-0 z-10 bg-[hsl(224_45%_9%)]">{w.created_at?.slice(0,10)}</td>
                  <td className="p-2 font-mono text-amber-300">{fmt(w.amount)}</td>
                  <td className="p-2 text-muted-foreground text-xs">{w.note || "—"}</td>
                  <td className="p-2"><Badge className={`gap-1 ${withStatusTone(w.status)}`}>{withStatusIcon(w.status)}{w.status || "pending"}</Badge></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Upcoming assignments */}
      <Card className="p-6 bg-[hsl(224_45%_9%)] border-border">
        <div className="flex items-center gap-2 mb-4 text-amber-400"><CalendarClock className="w-4 h-4"/><div className="font-display text-xl font-bold">Upcoming trips & itinerary</div>
          <Badge variant="outline" className="ml-auto border-amber-500/30 text-amber-300 text-xs">{upcoming.length} upcoming</Badge>
        </div>
        {upcoming.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">No upcoming trips assigned yet. Your admin will add them here.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {upcoming.map((a) => (
              <div key={a.id} data-testid={`asg-${a.id}`} className="p-4 rounded-lg border border-amber-500/30 bg-amber-500/[0.05]">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="font-semibold">{a.trip_title || "Upcoming trip"}</div>
                  <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/40">{a.status || "upcoming"}</Badge>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div><div className="text-[10px] uppercase text-muted-foreground">Trip date</div><div className="font-mono">{a.trip_date || "—"}{a.end_date ? ` → ${a.end_date}` : ""}</div></div>
                  <div><div className="text-[10px] uppercase text-muted-foreground">Client</div><div>{a.client?.name || "—"}</div></div>
                </div>
                {a.client && (a.client.mobile || a.client.whatsapp) && (
                  <div className="mt-3 flex flex-wrap gap-2 text-xs">
                    {a.client.mobile && (
                      <a href={`tel:${a.client.mobile}`} className="inline-flex items-center gap-1 px-2 py-1 rounded border border-border bg-[hsl(224_40%_12%)] hover:border-amber-500/40">
                        <Phone className="w-3 h-3 text-amber-300"/> <span className="font-mono">{a.client.mobile}</span>
                      </a>
                    )}
                    {a.client.whatsapp && (
                      <a href={`https://wa.me/${a.client.whatsapp.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-2 py-1 rounded border border-emerald-500/40 text-emerald-300 bg-emerald-500/5">
                        <MessageCircle className="w-3 h-3"/> <span className="font-mono">{a.client.whatsapp}</span>
                      </a>
                    )}
                  </div>
                )}
                {a.itinerary && (
                  <div className="mt-3 p-3 rounded bg-[hsl(224_40%_11%)] border border-border">
                    <div className="flex items-center gap-1 text-[10px] uppercase text-muted-foreground mb-1"><ScrollText className="w-3 h-3"/> Itinerary</div>
                    <div className="text-xs whitespace-pre-line">{a.itinerary}</div>
                  </div>
                )}
                {a.notes && <div className="mt-2 text-[11px] text-muted-foreground">{a.notes}</div>}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Submit a customer trip + my submissions */}
      <Card className="p-6 bg-[hsl(224_45%_9%)] border-border">
        <div className="flex items-center gap-2 mb-4 text-amber-400">
          <Send className="w-4 h-4"/>
          <div className="font-display text-xl font-bold">Submit a customer trip</div>
          <Badge variant="outline" className="ml-auto border-amber-500/30 text-amber-300 text-xs">
            {(s.submitted_trips || []).length} submitted · {(s.submitted_trips || []).filter((x) => x.status === "confirmed").length} confirmed · Bonus {fmt(s.bonus_total || 0)}
          </Badge>
        </div>
        <div className="mb-4">
          <Dialog open={submitOpen} onOpenChange={setSubmitOpen}>
            <DialogTrigger asChild>
              <Button data-testid="dp-new-submit" className="bg-amber-500 text-[hsl(224_60%_6%)] hover:bg-amber-400 font-semibold gap-2">
                <Send className="w-4 h-4"/> New trip submission
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[hsl(224_45%_9%)] border-border max-w-2xl">
              <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><Send className="w-5 h-5 text-amber-400"/> Submit a customer trip</DialogTitle></DialogHeader>
              <form onSubmit={submitTrip} className="grid grid-cols-2 gap-3 max-h-[70vh] overflow-y-auto pr-1">
                <div className="col-span-2 text-[11px] text-amber-200/80 rounded-md border border-amber-500/30 bg-amber-500/10 p-2">
                  Once submitted, this trip cannot be edited by you. Admin will confirm or reject and may add a bonus.
                </div>
                <div><Label className="text-[10px] uppercase text-muted-foreground">Customer name</Label>
                  <Input required value={sub.customer_name} onChange={(e) => setSub({ ...sub, customer_name: e.target.value })} data-testid="sub-name" className="bg-[hsl(224_40%_12%)] mt-1"/></div>
                <div><Label className="text-[10px] uppercase text-muted-foreground">Customer contact</Label>
                  <Input required value={sub.customer_contact} onChange={(e) => setSub({ ...sub, customer_contact: e.target.value })} data-testid="sub-contact" className="bg-[hsl(224_40%_12%)] mt-1"/></div>
                <div><Label className="text-[10px] uppercase text-muted-foreground">No. of days</Label>
                  <Input required type="number" min="1" value={sub.no_of_days} onChange={(e) => setSub({ ...sub, no_of_days: e.target.value })} data-testid="sub-days" className="bg-[hsl(224_40%_12%)] mt-1"/></div>
                <div><Label className="text-[10px] uppercase text-muted-foreground">No. of passengers</Label>
                  <Input required type="number" min="1" value={sub.no_of_passengers} onChange={(e) => setSub({ ...sub, no_of_passengers: e.target.value })} data-testid="sub-pax" className="bg-[hsl(224_40%_12%)] mt-1"/></div>
                <div><Label className="text-[10px] uppercase text-muted-foreground">Trip offer value ₹</Label>
                  <Input required type="number" min="0" step="0.01" value={sub.trip_offer_value} onChange={(e) => setSub({ ...sub, trip_offer_value: e.target.value })} data-testid="sub-offer" className="bg-[hsl(224_40%_12%)] mt-1"/></div>
                <div><Label className="text-[10px] uppercase text-muted-foreground">Trip date</Label>
                  <Input required type="date" value={sub.trip_date} onChange={(e) => setSub({ ...sub, trip_date: e.target.value })} data-testid="sub-date" className="bg-[hsl(224_40%_12%)] mt-1"/></div>
                <div className="col-span-2 md:col-span-1"><Label className="text-[10px] uppercase text-muted-foreground">Pickup location</Label>
                  <Input required value={sub.pickup_location} onChange={(e) => setSub({ ...sub, pickup_location: e.target.value })} data-testid="sub-pickup" className="bg-[hsl(224_40%_12%)] mt-1"/></div>
                <div className="col-span-2 md:col-span-1"><Label className="text-[10px] uppercase text-muted-foreground">Drop location</Label>
                  <Input required value={sub.drop_location} onChange={(e) => setSub({ ...sub, drop_location: e.target.value })} data-testid="sub-drop" className="bg-[hsl(224_40%_12%)] mt-1"/></div>
                <div className="col-span-2 flex justify-end">
                  <Button type="submit" data-testid="sub-save" className="bg-amber-500 text-[hsl(224_60%_6%)] hover:bg-amber-400 font-semibold">Submit trip</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="-mx-6 md:mx-0 overflow-x-auto">
          <table className="w-full min-w-[820px] text-sm">
            <thead><tr className="text-[10px] uppercase tracking-wider text-muted-foreground bg-[hsl(224_40%_11%)]">
              {["Date","Customer","Route","Days · Pax","Offer","Bonus","Status","Reason"].map((h, i) => (
                <th key={h} className={`text-left p-3 ${i === 0 ? "sticky left-0 z-10 bg-[hsl(224_40%_11%)] min-w-[110px]" : ""}`}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {(s.submitted_trips || []).length === 0 ? (
                <tr className="bg-[hsl(224_45%_9%)]"><td colSpan={8} className="p-6 text-center text-muted-foreground">No submissions yet. Tap "New trip submission" to send one to admin.</td></tr>
              ) : (s.submitted_trips || []).map((t) => {
                const tone = t.status === "confirmed" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"
                          : t.status === "cancelled" ? "bg-rose-500/15 text-rose-300 border-rose-500/40"
                          : "bg-amber-500/15 text-amber-300 border-amber-500/40";
                return (
                  <tr key={t.id} data-testid={`dp-sub-${t.id}`} className="border-t border-border bg-[hsl(224_45%_9%)]">
                    <td className="p-3 font-mono text-xs sticky left-0 z-10 bg-[hsl(224_45%_9%)] min-w-[110px]">{t.trip_date}</td>
                    <td className="p-3">{t.customer_name}<div className="text-[10px] font-mono text-muted-foreground">{t.customer_contact}</div></td>
                    <td className="p-3 text-xs">{t.pickup_location} → {t.drop_location}</td>
                    <td className="p-3 font-mono text-xs">{t.no_of_days}d · {t.no_of_passengers} pax</td>
                    <td className="p-3 font-mono text-sky-300">{fmt(t.trip_offer_value)}</td>
                    <td className="p-3 font-mono text-emerald-300">{t.bonus_amount > 0 ? fmt(t.bonus_amount) : "—"}</td>
                    <td className="p-3"><Badge className={tone}>{t.status || "pending"}</Badge></td>
                    <td className="p-3 text-xs text-rose-200 max-w-[240px]">{t.cancellation_reason || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Monthly earnings */}
      <Card className="p-6 bg-[hsl(224_45%_9%)] border-border">
        <div className="font-display text-xl font-bold mb-4">Month-wise earnings</div>
        <div className="-mx-6 md:mx-0 overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead><tr className="text-[10px] uppercase tracking-wider text-muted-foreground bg-[hsl(224_40%_11%)]">
              <th className="text-left p-3 sticky left-0 z-10 bg-[hsl(224_40%_11%)]">Month</th>
              <th className="text-left p-3">Trips</th>
              <th className="text-left p-3">Earnings (driver charge)</th>
              <th className="text-left p-3">Savings deducted</th>
            </tr></thead>
            <tbody>
              {(s.monthly_earnings || []).length === 0 ? (
                <tr className="bg-[hsl(224_45%_9%)]"><td colSpan={4} className="p-6 text-center text-muted-foreground">No trips yet.</td></tr>
              ) : s.monthly_earnings.map((m) => (
                <tr key={m.month} className="border-t border-border bg-[hsl(224_45%_9%)]">
                  <td className="p-3 font-mono sticky left-0 z-10 bg-[hsl(224_45%_9%)]">{m.month || "—"}</td>
                  <td className="p-3 font-mono">{m.trips}</td>
                  <td className="p-3 font-mono text-emerald-300">{fmt(m.earnings)}</td>
                  <td className="p-3 font-mono text-amber-300">{fmt(m.savings)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* My trips summary */}
      <Card className="p-6 bg-[hsl(224_45%_9%)] border-border">
        <div className="font-display text-xl font-bold mb-4">My trips</div>
        <div className="-mx-6 md:mx-0 overflow-x-auto">
          <table className="w-full min-w-[820px] text-sm">
            <thead><tr className="text-[10px] uppercase tracking-wider text-muted-foreground bg-[hsl(224_40%_11%)]">
              {["Trip ID","Start","End","Days","Trip Value","Driver Charge","Payable"].map((h, i) => (
                <th
                  key={h}
                  className={`text-left p-3 ${i === 0 ? "sticky left-0 z-10 bg-[hsl(224_40%_11%)] min-w-[140px]" : ""}`}
                >
                  {h}
                </th>
              ))}
            </tr></thead>
            <tbody>
              {s.trips.length === 0 ? <tr className="bg-[hsl(224_45%_9%)]"><td colSpan={7} className="p-6 text-center text-muted-foreground">No trips assigned</td></tr> :
                s.trips.map((t) => (
                  <tr key={t.id} className="border-t border-border bg-[hsl(224_45%_9%)]">
                    <td className="p-3 font-mono text-amber-300 sticky left-0 z-10 bg-[hsl(224_45%_9%)] min-w-[140px]">{t.trip_id}</td>
                    <td className="p-3 text-xs">{t.start_date}</td>
                    <td className="p-3 text-xs">{t.end_date}</td>
                    <td className="p-3 font-mono">{t.no_of_days}</td>
                    <td className="p-3 font-mono text-sky-300">{fmt(t.trip_value)}</td>
                    <td className="p-3 font-mono">{fmt(t.driver_charge)}</td>
                    <td className="p-3 font-mono text-emerald-300">{fmt(t.payable_to_driver)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
